define('models/AnalyticsDataV5',[
    'lib/knockout',
    'lib/lodash',
    'moment',
    'services/intervalService',
    'services/loggingService'
], function (
    ko,
    _,
    moment,
    intervalService,
    log
) {
    'use strict';

    var AnalyticsDataV5;

    // **TODO** (5/25/2014): This logic is added for Interactions 2014 and should
    // be refactored to utilize the right backend API.
    var CURRENT_OBSERVATIONS_METRICS = [
        "oWaiting",
        "oInteracting",
        "nActive",
        "nMembers",
        "oActive",
        "oMembers"
    ];

    // ===================================================================================
    // Private Methods
    // ===================================================================================

    /**
     * Determine if a source object has a value. This method will determine if the data
     * object is defined regardless if the object argument is a plain object or an
     * instance of AnalyticsData.
     *
     * @param  {Object|AnalyticsData}  obj The object to check if it is defined. If the
     *  argument is an instance of the AnalyticsData object, then this method will check
     *  if the rawData() attribute has a value.
     * @return {Boolean} true if the value is defined, false otherwise.
     */
    var isSourceObjectDefined = function (obj) {
        var source = ko.unwrap(obj);

        if (_.isUndefined(source) || _.isNull(source)) {
            // if the source data is a normal object and does not have a value, return
            // false
            return false;
        }

        if (source instanceof AnalyticsDataV5 && !source.rawData()) {
            // if the source is an AnalyticsDataObject but does not have its rawData
            // attribute defined, return false
            return false;
        }

        return true;
    };

    /**
     * This method will convert a stat notification payload into an object that better
     * conforms to the expected format of values in the rawData attribute.
     * It will return the transformed object.
     *
     * For example:
     *
     * # Before
     *
     * {
     *     "interval":"2014-04-29T11:00:00.000-04:00/2014-04-29T11:30:00.000-04:00",
     *     "entity":{
     *         "kind":"user",
     *         "id":"9982aa70-bff1-4c0b-8b4c-39d0318c89f2"
     *     },
     *     "metrics":{
     *         "tis.6a3af858-942f-489d-9700-5f9bcdcdae9b":{
     *             "count":1.0,
     *             "min":491986.0,
     *             "sum":491986.0,
     *             "max":491986.0
     *         }
     *      }
     * }
     *
     * # After
     *
     * {
     *     entity: {
     *         "kind":"user",
     *         "id":"9982aa70-bff1-4c0b-8b4c-39d0318c89f2"
     *     },
     *     data: [
     *         {
     *             "interval":"2014-04-29T11:00:00.000-04:00/2014-04-29T11:30:00.000-04:00",
     *             "metrics":{
     *             "tis.6a3af858-942f-489d-9700-5f9bcdcdae9b":{
     *                 "count":1.0,
     *                 "min":491986.0,
     *                 "sum":491986.0,
     *                 "max":491986.0
     *             }
     *         }
     *     ]
     * }
     *
     * @param  {Object} notificationObject The raw object representing the stat
     *  notification
     * @return {Object} The transformed object.
     */
    var convertNotificationObjectToRawDataObject = function (notificationObject) {
        var result = _.pick(notificationObject, "entity");

        var intervalObj = _.pick(notificationObject, "interval", "metrics");
        intervalObj.metrics = _.omit(intervalObj.metrics, CURRENT_OBSERVATIONS_METRICS);

        result.data = [
            intervalObj
        ];

        result.currentObservations = _.pick(notificationObject.metrics, CURRENT_OBSERVATIONS_METRICS);

        // handle new member objects
        if (_.isEmpty(result.currentObservations) && !_.isUndefined(notificationObject.currentObservations)) {
            result.currentObservations = _.pick(notificationObject.currentObservations, CURRENT_OBSERVATIONS_METRICS);
        }

        return result;
    };

    var generateRawData = function (data) {
        var rawData = data;

        if (!_.isEmpty(rawData) && !_.has(rawData, "data") && _.has(rawData, "entity")) {
            rawData = convertNotificationObjectToRawDataObject(rawData);
        }

        return rawData;
    };

    /**
     * A wrapper method to help set the value for the rawData attribute of an
     * AnalyticsData object. This method helps to seemlessly set the rawData attribute in
     * regardless of whether the dataObj argument represents a stat notification or an
     * analytics query response.
     *
     * @param {AnalyticsData} destinationObj The AnalyticsData object whose rawData()
     *  attribute will be set
     * @param {Object} newValue The value to set the rawData attribute to.
     */
    var setRawData = function (destinationObj, newValue) {
        var newRawData = generateRawData(newValue);

        ko.unwrap(destinationObj).rawData(newRawData);
    };

    /**
     * Combine the raw analytics data from a source object into an instance of
     * AnalyticsData. This method will seemlessly combine source objects regardless of
     * whether the data is from another AnalyticsData object, a query response payload, or
     * a stat notification.
     *
     * @param  {AnalyticsData} destinationData-  An instance of AnalyticsData who will have
     *  its rawData attribute updated.
     * @param  {Object|AnalyticeData} sourceData - The analytics data to combine into the
     *  destination argument.
     * @param  {Function} strategyFunction - A function that will be executed to determine
     *  how the combination of two existing data sets should occur. This function will
     *  take the existing interval object in the destination and the interval object from
     *  the source to determine how the source should be combined with the existing
     *  interval metrics data in the destination. This is primarily used to determine if a
     *  fill or a merge should occur.
     */
    var combineRawAnalyticsData = function (destinationData, sourceData, strategyFunction) {
        var destination = ko.unwrap(destinationData);

        if (!destination) {
            destination = new AnalyticsDataV5();
        }

        var destinationRawData = ko.unwrap(destination).rawData();
        var source;

        if (!isSourceObjectDefined(sourceData)) {
            log.debug("The source data was undefined or null when trying to combine analytics data", destinationData, sourceData, strategyFunction);
            return null; // don't do anything if source is undefined
        }

        if (_.isPlainObject(sourceData)) {
            if (_.has(sourceData, "results")) {
                source = sourceData.results[0];
            } else {
                source = sourceData;
            }
        } else {
            source = ko.unwrap(sourceData).rawData();
        }

        if (!destinationRawData || !destinationRawData.data) {
            // If the destination raw data is not defined, just set its value
            setRawData(destination, source);
        } else {
            var combineSourceObjectIntoDestinationObject = function (sourceIntervalObj) {
                var intervalDestination = _.find(destinationRawData.data, {interval: sourceIntervalObj.interval});
                var sourceInterval = sourceIntervalObj;
                // var sourceCurrentObservations = destinationRawData.currentObservations;

                if (sourceIntervalObj.entity) {
                    // convert the stat notification to an interval object that conforms
                    // to the expected format for the rawData attribute.
                    sourceInterval = _.pick(sourceIntervalObj, "interval", "metrics");

                    // **TODO** (5/25/2014): This logic is added for Interactions 2014 and should
                    // be refactored to utilize the right backend API.
                    sourceInterval.metrics = _.omit(sourceInterval.metrics, CURRENT_OBSERVATIONS_METRICS);
                }

                if (_.isUndefined(intervalDestination)) {
                    // If an interval is not found then add the new interval to the
                    // rawDataToFill object's collection of metrics.
                    destinationRawData.data.push(sourceInterval);
                } else {
                    strategyFunction(intervalDestination.metrics, sourceInterval.metrics);
                }
            };

            if (source.data) {
                _.each(source.data, combineSourceObjectIntoDestinationObject);
            } else {
                combineSourceObjectIntoDestinationObject(source);
            }

            // Combine "currentObservation" metrics
            // **TODO** (5/25/2014): This logic is added for Interactions 2014 and should
            // be refactored to utilize the right backend API.
            var sourceCurrentObservations = source.currentObservations;

            if (source.currentObservations) {
                sourceCurrentObservations = _.pick(source.currentObservations, CURRENT_OBSERVATIONS_METRICS);
            } else if (source.entity) {
                sourceCurrentObservations = _.pick(source.metrics, CURRENT_OBSERVATIONS_METRICS);
            }

            if (!destinationRawData.currentObservations) {
                destinationRawData.currentObservations = {};
            }

            strategyFunction(destinationRawData.currentObservations, sourceCurrentObservations);

            // Tell subscribers that the observable value has changed.
            ko.unwrap(destination).rawData.valueHasMutated();
        }

        if (ko.isObservable(destinationData)) {
            // initialize destinationData into an analytics data object if it does not
            // have a value yet and is an observable.
            destinationData(destination);
        }

        return destination;
    };

    // ===================================================================================
    // Class Definition
    // ===================================================================================

    /*
     * A private utility class that helps expose reusable computations on a single result object
     * from the analytics service. The result object contains all of the data objects inside of an array named 'data'.
     * The data array can contain multiple objects, each consisting of an interval and metrics.
     * This object provides helper methods to compute sums and averages across multiple response objects.
     * This *may* become obsolete as the analytics api now returns averages and sums.
     *
     * See Call Flow and Metrics from the Analytics API V2:
     *
     * https://confluence.inin.com/pages/viewpage.action?pageId=61452876
     */
    AnalyticsDataV5 = function (rawData) {
        var self = this;

        self.rawData = ko.observable(generateRawData(rawData));

        self.entityId = ko.computed(function () {
            if (_.isEmpty(self.rawData())) {
                return undefined;
            }

            if (self.rawData().entity) {
                return self.rawData().entity.id;
            }

            // get the dynamic key that contains the entity's id
            var queryEntityData = _.omit(self.rawData(), "data");

            // Pull out the value from the entity's key name (which is dynamic) and return
            // it as the id.
            return _.values(queryEntityData)[0];
        });

        self.getCurrentObservationMetric = function (key) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().currentObservations)) {
                return null;
            }

            return self.rawData().currentObservations[key];
        };

        /*
         * Slice the data currently stored in rawData() into a subset determined by the
         * interval provided as an argument to this getDataForInterval().
         *
         * @param {String|Moment} A string representing an ISO8601 interval or a moment
         *                        object that indicates the start of the interval
         * @param {Moment} If the first argument is a moment object, this argument is
         *                 required to included. It should be a moment object that
         *                 indicates the end of the interval.
         * @returns A new instance of AnalyticsData that contains the subset of data found
         *          for the requested interval. If not data is found or there is a problem
         *          with the arguments, null will be returned.
         */
        self.getDataForInterval = function (startInterval, endInterval) {
            if (!self.rawData() || !self.rawData().data) {
                log.debug("rawData and/or rawData().data does not have a value", self.rawData());
                return null;
            }

            if (!startInterval || (typeof startInterval != "string" && !endInterval)) {
                log.error("getDataForInterval() - The interval arguments to the method must be an ISO8601 interval string or moment objects.");
                return null;
            }

            var startMoment = startInterval;
            var endMoment = endInterval;

            if (_.isString(startInterval)) {
                var intervalStrs = intervalService.splitIntervalString(startInterval);

                if (_.isNull(intervalStrs)) {
                    return null;
                }

                startMoment = moment(intervalStrs[0]);
                endMoment = moment(intervalStrs[1]);
            }

            if (!startMoment.isValid() || !endMoment.isValid()) {
                log.error("getDataForInterval() - Both arguments to getDataForInterval must be moment objects");
                return null;
            }

            var filteredData = _.filter(self.rawData().data, function (obj) {
                var times = intervalService.splitIntervalString(obj.interval);

                var startTime = times[0];
                var endTime = times[1];

                var isAfterStart = startMoment.isBefore(startTime) || startMoment.isSame(startTime);
                var isBeforeEnd = endMoment.isAfter(endTime) || endMoment.isSame(endTime);

                if (isAfterStart && isBeforeEnd) {
                    return true;
                }

                return false;
            });

            if (filteredData.length === 0) {
                return null;
            }

            var newRawData = _.clone(self.rawData(), true);

            newRawData.data = filteredData;

            return new AnalyticsDataV5(newRawData);
        };

        /**
         * Get the object for a given key in the "metrics" object for an interval. The
         * object returned is determined by the provided start and end interval arguments.
         *
         * This mehtod will look for an exact match in the intervals provided to it and
         * the intervals in the collection of data that the instance is managing. Fuzzy
         * matching of intervals is not supported because determining the exact metric
         * object to return does not work if mupltiple interval objects are found.
         *
         * @param  {String} metricKey     The key name of the metric to look for.
         * @param  {[type]} startInterval A string representing an ISO8601 interval or a
         *  moment object that indicates the start of the interval
         * @param  {[type]} endInterval   If the first argument is a moment object, this
         *  argument is required. It should be a moment object that represents the end
         *  time of the interval.
         *
         * @return {Object|Null} An object containing the statistics that represent the
         *  requested metric key. If an object is not found, null is returned.
         */
        self.getMetricObjectForInterval = function (metricKey, startInterval, endInterval) {
            if (!self.rawData() || !self.rawData().data) {
                log.debug("rawData and/or rawData().data does not have a value", self.rawData());
                return null;
            }

            if (!startInterval || (!_.isString(startInterval) && !endInterval)) {
                log.error("getDataForInterval() - The interval arguments to the method must be an ISO8601 interval string or moment objects.");
                return null;
            }

            var startMoment = startInterval;
            var endMoment = endInterval;

            if (_.isString(startInterval)) {
                var intervalStrs = intervalService.splitIntervalString(startInterval);

                if (_.isNull(intervalStrs)) {
                    return null;
                }

                startMoment = moment(intervalStrs[0]);
                endMoment = moment(intervalStrs[1]);
            }

            if (!startMoment.isValid() || !endMoment.isValid()) {
                log.error("getDataForInterval() - Both arguments to getDataForInterval must be moment objects");
                return null;
            }

            var metricObj = _.find(self.rawData().data, function (dataObj) {
                var times = intervalService.splitIntervalString(dataObj.interval);

                var startTime = times[0];
                var endTime = times[1];

                return (startMoment.isSame(startTime) && endMoment.isSame(endTime));
            });

            if (_.isUndefined(metricObj) || !_.has(metricObj.metrics, metricKey)) {
                return null;
            }

            return metricObj.metrics[metricKey];
        };

        /*
        * Get a summation of the "count" attribute across all intervals for a given
        * statistic.
        *
        * @param {String} The key name of the statistic to use as a sum.
        * @returns An integer representing that sum of the value for each instance of the
        *          "count" attribute across all intervals. If the key name or count
        *          attribute for the statistic is not found then null will be returned.
        */
        self.sumCount = function (statName) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var hasStat = false;
            var totalCount = 0;

            totalCount = _.reduce(self.rawData().data, function(countSum, intervalObj) {
                if (intervalObj.metrics[statName]) {
                    hasStat = true;
                    return countSum + intervalObj.metrics[statName].count;
                }

                return countSum;
            }, 0);

            if (!hasStat) {
                return null;
            }

            return totalCount;
        };

        /*
        * Get a summation of the "sum" attribute across all intervals for a given
        * statistic.
        *
        * @param {String} The key name of the statistic to use.
        * @returns An integer representing that sum of the value for each instance of the
        *          "sum" attribute across all intervals. If the key name or sum
        *          attribute for the statistic is not found then null will be returned.
        */
        self.sum = function (statName) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var hasStat = false;
            var totalValue = 0;

            totalValue = _.reduce(self.rawData().data, function(valueSum, intervalObj) {
                if (intervalObj.metrics[statName]) {
                    hasStat = true;
                    return valueSum + intervalObj.metrics[statName].sum;
                }

                return valueSum;
            }, 0);

            if (!hasStat) {
                return null;
            }

            return totalValue;
        };

        self.sumMetricAttribute = function (metricName, attribute) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var hasStat = false;
            var totalCount = 0;

            totalCount = _.reduce(self.rawData().data, function(countSum, intervalObj) {
                if (!_.isUndefined(intervalObj.metrics) && !_.isUndefined(intervalObj.metrics[metricName])) {
                    hasStat = true;
                    return countSum + intervalObj.metrics[metricName][attribute];
                }

                return countSum;
            }, 0);

            if (!hasStat || _.isNaN(totalCount)) {
                return null;
            }

            return totalCount;
        };

        self.avgMetricAttribute = function (metricName, attribute) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var hasStat = false;
            var totalCount = 0;
            var dataCount = 0;

            totalCount = _.reduce(self.rawData().data, function(countSum, intervalObj) {
                dataCount++;
                if (intervalObj.metrics[metricName]) {
                    hasStat = true;
                    return countSum + intervalObj.metrics[metricName][attribute];
                }

                return countSum;
            }, 0);

            if (!hasStat || _.isNaN(totalCount)) {
                return null;
            }
            if (dataCount > 0) {
                return totalCount / dataCount;
            }
            return totalCount;
        };

        /*
        * Compute the average value of a statistic.
        *
        * @param {String} The key name of the statistic to use as a sum.
        * @returns An integer representing that average value across all intervals.
        *          If the key name for the statistic is not found then null will be
        *          returned.
        */
        self.avg = function (statName) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var average;
            var totalCount = self.sumCount(statName);
            var totalValue = self.sum(statName);

            if (!totalCount || !totalValue) {
                return null;
            }

            average = totalValue / totalCount;

            return average;
        };
    };

    // ===================================================================================
    // Static Class Methods
    // ===================================================================================

    /**
     * Fills missing pieces of data in an existing AnalyticsData object's rawData
     * attribute with data from a source object. Existing static fields will NOT be
     * overwritten.
     *
     * @param  {AnalyticsData|Observable} destinationData - The instance of an AnalyticsData object
     *  to merge data into.
     *
     *  *Note* - If this value is an observable that is not initialized,
     *  then this method will create an empty instance of AnalyticsData and set the
     *  observable's value to this new instance.
     * @param  {Object|AnalyticsData} sourceData - The source data to merge into
     *  destinationData. This data can be an object that represents the payload returned
     *  from a query to analytics service or an existing instance of an AnalyticsData
     *  object.
     * @return The updated value of the object passed into the "destinationData" argument.
     */
    AnalyticsDataV5.fill = function (destinationData, sourceData) {
        // return combineRawAnalyticsData(destinationData, sourceData, function (intervalDestinationObj, sourceIntervalObj) {
        return combineRawAnalyticsData(destinationData, sourceData, function (destination, source) {
            // If an interval is found, fill the missing pieces of data while ensuring
            // that existing data is not overwritten.
            _.forIn(source, function(value, key) {
                if (_.has(destination, key)) {
                    _.defaults(destination[key], value);
                } else {
                    destination[key] = value;
                }
            });
        });
    };

    /**
     * Merges raw data into an existing AnalyticsData object's rawData attribute. Static
     * fields will be overwritten.
     *
     * @param  {AnalyticsData|Observable} destinationData - The instance of an AnalyticsData object
     *  to merge data into.
     *
     *  *Note* - If this value is an observable that is not initialized,
     *  then this method will create an empty instance of AnalyticsData and set the
     *  observable's value to this new instance.
     * @param  {Object|AnalyticsData} sourceData - The source data to merge into
     *  destinationData. This data can be an object that represents the payload returned
     *  from a query to analytics service or an existing instance of an AnalyticsData
     *  object.
     * @return The updated value of the object passed into the "destinationData" argument.
     */
    AnalyticsDataV5.merge = function (destinationData, sourceData) {
        // return combineRawAnalyticsData(destinationData, sourceData, function (intervalDestinationObj, sourceIntervalObj) {
        return combineRawAnalyticsData(destinationData, sourceData, function (destination, source) {
            // If an interval is found, merge the pieces of data
            _.assign(destination, source);
        });
    };

    return AnalyticsDataV5;
});
